import React, { useCallback, useRef, useState } from "react";
import Papa from "papaparse";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, FileText, X, AlertTriangle, Users } from "lucide-react";

export interface CsvStudent {
    email: string;
    first_name: string;
    last_name: string;
}

interface CsvStudentUploaderProps {
    students: CsvStudent[];
    onStudentsChange: (students: CsvStudent[]) => void;
    readOnly?: boolean;
}

const COLUMN_MAP: Record<string, keyof CsvStudent> = {
    email: "email",
    mail: "email",
    courriel: "email",
    "e-mail": "email",
    first_name: "first_name",
    firstname: "first_name",
    prenom: "first_name",
    prénom: "first_name",
    last_name: "last_name",
    lastname: "last_name",
    nom: "last_name",
};

function mapHeaders(raw: Record<string, string>): CsvStudent | null {
    const mapped: Partial<CsvStudent> = {};
    for (const [key, value] of Object.entries(raw)) {
        const normalized = key.trim().toLowerCase().replace(/['"]/g, "");
        const field = COLUMN_MAP[normalized];
        if (field) mapped[field] = (value ?? "").trim();
    }
    if (!mapped.email) return null;
    return {
        email: mapped.email,
        first_name: mapped.first_name ?? "",
        last_name: mapped.last_name ?? "",
    };
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseCsvText(text: string): { students: CsvStudent[]; warnings: string[] } {
    const result = Papa.parse<Record<string, string>>(text.trim(), {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.trim(),
    });

    const warnings: string[] = [];
    const seen = new Set<string>();
    const students: CsvStudent[] = [];
    let duplicates = 0;
    let invalid = 0;

    for (const row of result.data) {
        const mapped = mapHeaders(row);
        if (!mapped) { invalid++; continue; }

        const email = mapped.email.toLowerCase().trim();
        if (!EMAIL_RE.test(email)) { invalid++; continue; }
        if (seen.has(email)) { duplicates++; continue; }

        seen.add(email);
        students.push({ ...mapped, email });
    }

    if (duplicates > 0) warnings.push(`${duplicates} doublon(s) ignoré(s)`);
    if (invalid > 0) warnings.push(`${invalid} ligne(s) invalide(s) ignorée(s)`);

    return { students, warnings };
}

const CsvStudentUploader: React.FC<CsvStudentUploaderProps> = ({ students, onStudentsChange, readOnly }) => {
    const fileRef = useRef<HTMLInputElement>(null);
    const [pasteText, setPasteText] = useState("");
    const [warnings, setWarnings] = useState<string[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);

    const handleParsedText = useCallback((text: string) => {
        const { students: parsed, warnings: w } = parseCsvText(text);
        setWarnings(w);
        if (parsed.length > 0) {
            onStudentsChange(parsed);
            setPasteText("");
        } else {
            setWarnings(prev => [...prev, "Aucun étudiant valide trouvé"]);
        }
    }, [onStudentsChange]);

    const handleFile = useCallback((file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            if (text) handleParsedText(text);
        };
        reader.readAsText(file);
    }, [handleParsedText]);

    const onDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) handleFile(file);
    }, [handleFile]);

    const removeStudent = (email: string) => {
        onStudentsChange(students.filter(s => s.email !== email));
    };

    // Read-only: just show the table
    if (readOnly) {
        return (
            <div className="space-y-3">
                <div className="flex items-center gap-2 mb-2">
                    <Badge variant="secondary" className="bg-violet-50 text-violet-700 border-violet-200 rounded-lg px-3 py-1 text-xs font-bold">
                        <FileText className="h-3 w-3 mr-1.5" />
                        {students.length} étudiant(s) depuis fichier CSV
                    </Badge>
                </div>
                {students.length > 0 && (
                    <div className="border border-slate-100 rounded-2xl overflow-hidden max-h-[300px] overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50 sticky top-0">
                                <tr>
                                    <th className="text-left px-3 py-2 font-bold text-slate-500">Email</th>
                                    <th className="text-left px-3 py-2 font-bold text-slate-500">Prénom</th>
                                    <th className="text-left px-3 py-2 font-bold text-slate-500">Nom</th>
                                </tr>
                            </thead>
                            <tbody>
                                {students.map((s) => (
                                    <tr key={s.email} className="border-t border-slate-50">
                                        <td className="px-3 py-2 text-slate-700 font-medium">{s.email}</td>
                                        <td className="px-3 py-2 text-slate-600">{s.first_name}</td>
                                        <td className="px-3 py-2 text-slate-600">{s.last_name}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Upload zone */}
            {students.length === 0 && (
                <>
                    <div
                        className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all cursor-pointer ${isDragOver ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300 bg-slate-50/30"}`}
                        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                        onDragLeave={() => setIsDragOver(false)}
                        onDrop={onDrop}
                        onClick={() => fileRef.current?.click()}
                    >
                        <Upload className={`h-8 w-8 mx-auto mb-3 ${isDragOver ? "text-primary" : "text-slate-300"}`} />
                        <p className="text-sm font-bold text-slate-600">
                            Glissez un fichier CSV ici
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                            ou cliquez pour sélectionner — colonnes attendues : email, prénom, nom
                        </p>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".csv,.txt,.tsv"
                            className="hidden"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleFile(file);
                                e.target.value = "";
                            }}
                        />
                    </div>

                    <div className="space-y-2">
                        <p className="text-xs font-bold text-slate-500 ml-1">Ou collez le contenu CSV :</p>
                        <textarea
                            className="w-full h-24 rounded-xl border border-slate-200 bg-slate-50/50 text-xs font-mono p-3 resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                            placeholder={"email;prénom;nom\njean@example.com;Jean;Dupont\nmarie@example.com;Marie;Martin"}
                            value={pasteText}
                            onChange={(e) => setPasteText(e.target.value)}
                        />
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="rounded-lg text-xs font-bold"
                            disabled={!pasteText.trim()}
                            onClick={() => handleParsedText(pasteText)}
                        >
                            <FileText className="h-3.5 w-3.5 mr-1.5" />
                            Parser
                        </Button>
                    </div>
                </>
            )}

            {/* Warnings */}
            {warnings.length > 0 && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-700 font-medium space-y-0.5">
                        {warnings.map((w, i) => <p key={i}>{w}</p>)}
                    </div>
                </div>
            )}

            {/* Student table */}
            {students.length > 0 && (
                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-emerald-500" />
                            <span className="text-sm font-bold text-slate-700">{students.length} étudiant(s) valide(s)</span>
                        </div>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-xs text-slate-400 hover:text-red-500 font-bold"
                            onClick={() => { onStudentsChange([]); setWarnings([]); }}
                        >
                            Réinitialiser
                        </Button>
                    </div>
                    <div className="border border-slate-100 rounded-2xl overflow-hidden max-h-[300px] overflow-y-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-slate-50 sticky top-0">
                                <tr>
                                    <th className="text-left px-3 py-2 font-bold text-slate-500">Email</th>
                                    <th className="text-left px-3 py-2 font-bold text-slate-500">Prénom</th>
                                    <th className="text-left px-3 py-2 font-bold text-slate-500">Nom</th>
                                    <th className="w-8"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {students.map((s) => (
                                    <tr key={s.email} className="border-t border-slate-50 group hover:bg-slate-50/50">
                                        <td className="px-3 py-2 text-slate-700 font-medium">{s.email}</td>
                                        <td className="px-3 py-2 text-slate-600">{s.first_name}</td>
                                        <td className="px-3 py-2 text-slate-600">{s.last_name}</td>
                                        <td className="px-2 py-2">
                                            <button
                                                type="button"
                                                className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-all"
                                                onClick={() => removeStudent(s.email)}
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CsvStudentUploader;
